import type { QueryClient } from "@tanstack/react-query";

import { analytics } from "@/lib/analytics/analytics";
import { clearAnalyticsSessionUserId } from "@/lib/analytics/session-user";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/queries/useCurrentUser";
import { invalidateDashboardQueries } from "@/lib/dashboard/invalidate-dashboard-queries";

/** Drop cached user — required after logout because `codex_auth` is httpOnly. */
export function clearClientAuthCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY });
}

/** Clear profile + workspace caches when the signed-in identity changes. */
export function resetClientIdentityCaches(queryClient: QueryClient): void {
  clearAnalyticsSessionUserId();
  analytics.reset();
  clearClientAuthCache(queryClient);
  invalidateDashboardQueries(queryClient);
}

/**
 * Clears the browser session and all client-side auth caches.
 * `codex_auth` is httpOnly — JS cannot read it; always reset query state on logout.
 */
export async function performClientLogout(
  queryClient: QueryClient,
): Promise<void> {
  try {
    await fetch("/api/auth", { method: "DELETE", cache: "no-store" });
  } finally {
    resetClientIdentityCaches(queryClient);
  }
}
